import { RESOURCE_TIER, volumePerUnitM3 } from "./resourceCatalog";
import { MARKET_ITEMS } from "./data/marketItems";

// Base ownership/storage data model (design doc Section 11a/12, revised
// per 16.36's "pick reasonable placeholders" direction). Lot count (fixed
// at 9, no tier scaling) and max-Bases-per-system (one per buildable
// planet, no separate cap) are now locked -- see 16.51; storage-capacity
// numbers below are still real placeholders pending Section 15's economy
// pass. A Base record is created the instant a planet lease is purchased
// (expeditionScreen.ts's Buy Lease action) -- ownership itself is already
// real via state.ownedLeases; this is what that ownership actually HOLDS.
//
// Storage is segmented into named modules (Section 11a: "Base storage...
// segmented by storage module, each with its own m3 cap"), reusing the
// doc's own module names for the ones this pass actually has content
// for -- "Refined Ore"/"Crew Provisions"/"Fuel"/"Module Bay" are skipped
// since nothing in the game yet produces refined goods, models
// Provisions/Fuel consumption, or has a fitting system to bay modules
// from (16.16 keeps the fixed roster, see 16.36).
export type BaseStorageCategory = "Mineral Storage" | "Weapon Cache" | "Vault";

// Placeholder capacities (m3), clearly illustrative -- retune once the
// design doc actually locks Base storage numbers (Section 15's open
// "storage capacity numbers... not yet set").
export const BASE_STORAGE_CAPS: Record<BaseStorageCategory, number> = {
  "Mineral Storage": 5000,
  "Weapon Cache": 2000,
  Vault: 3000
};

export interface BaseRecord {
  leaseKey: string; // "<systemId>:<planetId>", matches state.ownedLeases
  systemId: string;
  planetId: string;
  // Same shape as ship cargo (state.cargo) -- item name -> quantity in
  // UNITS, not m3 (16.40) -- so a specific item's identity survives being
  // stored, not just an undifferentiated per-category total. Category caps
  // (above) are enforced by summing each item's real volume
  // (volumePerUnitM3), not a separate counter that could drift from the
  // real per-item ledger.
  storage: Record<string, number>;
  // Which of the fixed lot modules (16.42) have actually been built at
  // this Base -- starts empty on a fresh lease (Section 12: "predetermined
  // lots... every lot is tappable and shows the module it's earmarked
  // for" before anything's built there). Launchpad is exempt (see
  // BASE_LOT_BUILD_COST_CR) -- a Base needs a way to leave it before
  // anything else exists to build.
  builtModules: string[];
}

export function createBase(systemId: string, planetId: string): BaseRecord {
  return { leaseKey: `${systemId}:${planetId}`, systemId, planetId, storage: {}, builtModules: [] };
}

// Finds the Base (if any) the player currently owns at this exact
// System+Location -- the one real gate for "am I standing on my own
// land" that Cargo UI and Base View both need, kept in one place rather
// than duplicated as an inline .find().
export function findBaseAt(bases: BaseRecord[], systemId: string, planetId: string): BaseRecord | undefined {
  return bases.find((b) => b.systemId === systemId && b.planetId === planetId);
}

// Placeholder Build costs (16.42) -- credits only, matching the same gap
// ship purchases already have (PlayerShip.costResources is displayed,
// Section 12's own "resources + credits" is never actually charged
// anywhere, shipCardScreen.ts/shipyardScreen.ts). No number is locked
// anywhere in the doc for this either (Section 15: "detailed numeric/
// economic balancing for... building costs... framework decided, numbers
// not yet set") -- scaled loosely by how central each module is to normal
// play, cheaper than the 10,000 CR lease itself. Launchpad/Dock has no
// entry -- it's not a purchasable lot, it's what makes the Base a Base.
export const BASE_LOT_BUILD_COST_CR: Record<string, number> = {
  "Crew Quarters": 1500,
  "Fleet Roster": 1500,
  "Cargo Warehouse": 2000,
  "Resource Stockpile": 2000,
  "Mission Board": 2500,
  Armory: 3000,
  Market: 3000,
  "Captain's Lounge": 3500,
  Shipyard: 4000,
  // 16.81: the lot that makes a Base worth flying home to. Priced between
  // the Shipyard and Planetary Defenses -- it's a real capability (repair
  // with no yard fee at all, only materials), not another menu, but it
  // doesn't change the danger model the way Defenses do.
  "Repair Bay": 5000,
  // Priciest lot by design (16.70): it's the only one that changes the
  // danger model rather than opening a menu -- it makes the space around
  // your own planet genuinely safe. Section 6 promoted base defenses from
  // a "later" idea to a required building for a base to be safe at all.
  "Planetary Defenses": 6000
};

// Planetary Defenses (16.70) -- the module name, used as both the lot key
// above and the `builtModules` entry. Exported so callers can ask "does
// this base have defenses" without restating a string literal that would
// silently drift from the lot list. The behaviour itself lives in
// src/planetaryDefenses.ts.
export const PLANETARY_DEFENSES = "Planetary Defenses";

export function isLotBuilt(base: BaseRecord, moduleName: string): boolean {
  return base.builtModules.includes(moduleName);
}

// Mutates base.builtModules directly (same convention as
// transferToBase/transferFromBase's own base.storage mutation) --
// idempotent, a second call for an already-built lot is a harmless no-op.
export function buildLot(base: BaseRecord, moduleName: string): void {
  if (!base.builtModules.includes(moduleName)) {
    base.builtModules.push(moduleName);
  }
}

// Raw mining resources (resourceCatalog.ts) go to Mineral Storage;
// Munitions-category market goods go to the Weapon Cache; everything else
// (Salvage/Artifact/Contraband/Luxury/Industrial/Tech/Medical/Provisions)
// shares the general Vault -- a simple, defensible v1 split given the doc
// never specifies which goods belong in which named module.
export function storageCategoryFor(itemName: string): BaseStorageCategory {
  if (itemName in RESOURCE_TIER) {
    return "Mineral Storage";
  }
  const item = MARKET_ITEMS.find((i) => i.name === itemName);
  if (item?.category === "Munitions") {
    return "Weapon Cache";
  }
  return "Vault";
}

// Volume-weighted (16.40): base.storage holds unit counts, so each item's
// real contribution to the category's m3 cap is qty * volumePerUnitM3 --
// for anything that isn't real ore that's still just qty (volumePerUnitM3
// === 1), so this is a no-op change for Weapon Cache/Vault contents.
export function categoryUsedM3(base: BaseRecord, category: BaseStorageCategory): number {
  return Object.entries(base.storage).reduce(
    (sum, [name, qty]) => (storageCategoryFor(name) === category ? sum + qty * volumePerUnitM3(name) : sum),
    0
  );
}

export function categoryFreeM3(base: BaseRecord, category: BaseStorageCategory): number {
  return Math.max(0, BASE_STORAGE_CAPS[category] - categoryUsedM3(base, category));
}

// Both transfer functions take and return UNIT counts (16.40) -- pure and
// one-directional, capped only by the Base storage side -- callers (Cargo
// UI) are responsible for also capping by the ship's own free cargo space
// (freeCargoM3/freeOreHoldM3, acceptedMissions.ts) before/after calling
// these, the same division of responsibility depositMining already uses.
export function transferToBase(base: BaseRecord, itemName: string, unitsRequested: number): number {
  // The destination cap is a real volume limit, so the request is
  // converted to volume, clamped there, then converted back to the units
  // actually moved -- the one place a transfer function needs to know
  // about volume at all, since transferFromBase's destination (ship cargo)
  // is capped by the caller instead.
  const vpu = volumePerUnitM3(itemName);
  const freeVol = categoryFreeM3(base, storageCategoryFor(itemName));
  const movedVol = Math.max(0, Math.min(unitsRequested * vpu, freeVol));
  const movedUnits = movedVol / vpu;
  if (movedUnits > 0) {
    base.storage[itemName] = (base.storage[itemName] ?? 0) + movedUnits;
  }
  return movedUnits;
}

export function transferFromBase(base: BaseRecord, itemName: string, unitsRequested: number): number {
  const available = base.storage[itemName] ?? 0;
  const moved = Math.max(0, Math.min(unitsRequested, available));
  if (moved > 0) {
    const remaining = available - moved;
    if (remaining <= 0) {
      delete base.storage[itemName];
    } else {
      base.storage[itemName] = remaining;
    }
  }
  return moved;
}
